import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AssociatedCompanySection } from '../../../components/AddAssociatedAccountDialog/AssociatedCompanySection';
import STRING_KEYS from '../../../language/keys';
import { ErrorsStack } from '../../../components/ErrorsStack';
import { AccountInformationSection } from '../AccountInformationSection';
import { AccountPaidPlan } from '../AccountPaidPlan';
import { AccountOpenPosition } from '../AccountOpenPosition/account-open-position.component';
import { AccountAssociatedFields } from '../AccountEditPanelContainer';
import { AccountCreditsAndSeats } from '../AccountCreditsAndSeats/account-credits-and-seats.component';
import { AccountCreditAllocations } from '../AccountCreditsAllocations/account-credit-allocations.component';
import { AccountTrialOutreachToggle } from '../AccountTrialOutreachToggle';
import { AccountReferralPositions } from '../AccountReferralPositions';
import { DateValidationFields } from '../../../@types/Account';
import { PRICE_PLAIN_TYPE } from '../../../consts/enums';
import { style } from './style';

type AccountAssociatedCompanyProps = {
  linkedinUrl: string;
  accountAssociatedFields: AccountAssociatedFields;
  onValuesChange: (name: string, value: string | boolean) => void;
  onInputUploadImage: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadLoading: boolean;
  accountId: string;
  isErrorUploadImage: boolean;
  isErrorUploadImageMessage: string;
  onLoginAsAdminClick: () => void;
  viewMode: ViewMode;
  startDatePaidPlan: DateValidationFields;
  endDatePaidPlan: DateValidationFields;
  pricePlan: string;
  billing: string;
  onChangeDateField: (name: string, date: Date | string) => void;
  onChangePaidPlanStartDate: (name: string, date: Date | string) => void;
  isEditable: boolean;
  onAssociatedCompanyOpenClick: () => void;
  errorsValidation: string[];
};

export const AccountAssociatedCompany = ({
  accountAssociatedFields,
  onValuesChange,
  onInputUploadImage,
  isUploadLoading,
  accountId,
  isErrorUploadImage,
  isErrorUploadImageMessage,
  linkedinUrl,
  onLoginAsAdminClick,
  viewMode,
  pricePlan,
  startDatePaidPlan,
  endDatePaidPlan,
  billing,
  onChangeDateField,
  isEditable,
  onChangePaidPlanStartDate,
  onAssociatedCompanyOpenClick,
  errorsValidation,
}: AccountAssociatedCompanyProps): JSX.Element => {
  const { t: translate } = useTranslation();

  return (
    <>
      <Typography variant="h6" sx={ style.card.title.sx }>
        {translate(STRING_KEYS.ACCOUNTS_ASSOCIATED_COMPANY)}
      </Typography>
      {errorsValidation.length !== 0 && (
        <Box sx={ style.Box.sx }>
          <ErrorsStack errors={ errorsValidation } errorMessage="" />
        </Box>
      )}
      {isErrorUploadImage && (
        <ErrorsStack errors={ [] } errorMessage={ isErrorUploadImageMessage } />
      )}
      <AssociatedCompanySection
        name={ accountAssociatedFields.name || 'No company' }
        logo={ accountAssociatedFields.logo }
        onInputUploadImage={ onInputUploadImage }
        isDisabled
        isLoading={ isUploadLoading }
        linkedinUrl={ linkedinUrl }
        showIconLinkedin
        viewMode={ viewMode }
        isAssociatedCompany
        onCompanyChangeClick={ onAssociatedCompanyOpenClick }
      />
      <Divider sx={ style.card.divider.sx } />
      <AccountInformationSection
        displayNameFields={ accountAssociatedFields.displayName }
        accountStatusFields={ accountAssociatedFields.accountStatus }
        subDomainNameValues={ accountAssociatedFields.subDomainName }
        recruitingUsageTypeFields={ accountAssociatedFields.recruitingUsageType }
        onValuesChange={ onValuesChange }
        accountId={ accountId }
        onLoginAsAdminClick={ onLoginAsAdminClick }
        viewMode={ viewMode }
      />
      <AccountPaidPlan
        accountId={ accountId }
        isEditable={ isEditable }
        viewMode={ viewMode }
        pricePlan={ pricePlan }
        startDatePaidPlan={ startDatePaidPlan }
        endDatePaidPlan={ endDatePaidPlan }
        billing={ billing }
        onValuesChange={ onValuesChange }
        onChangeDateField={ onChangeDateField }
        onChangePaidPlanStartDate={ onChangePaidPlanStartDate }
      />
      <AccountTrialOutreachToggle
        value={ accountAssociatedFields.outreachEnabledForTrial }
        persistedValue={ accountAssociatedFields.persistedOutreachEnabledForTrial }
        pricingPlan={ pricePlan }
        onValuesChange={ onValuesChange }
        viewMode={ viewMode }
      />

      {accountAssociatedFields.isCreditsBased.value === 'true' && (
        <AccountCreditsAndSeats
          viewMode={ viewMode }
          pricingPlan={ pricePlan }
          accountAssociatedFields={ accountAssociatedFields }
          onValuesChange={ onValuesChange }
        />
      )}
      {accountAssociatedFields.isCreditsBased.value !== 'true' && pricePlan !== PRICE_PLAIN_TYPE.PayPerInterview && (
        <AccountOpenPosition
          viewMode={ viewMode }
          maximumOpenPositions={ accountAssociatedFields.maximumOpenPositions }
          onValuesChange={ onValuesChange }
        />
      )}
      <AccountReferralPositions accountId={ accountId } />

      {accountAssociatedFields.isCreditsBased && viewMode !== 'editable' && (
        <>
          <Divider />

          <AccountCreditAllocations accountId={ accountId } />
        </>
      )}
    </>
  );
};
